import type { BusinessContext } from "../../../../types/businessContext";
import type { HealthModelScopeDescriptor } from "./healthScoreHelpers";
import type { HealthModelScopeOption } from "./healthScoreSettingsState";

const summarizeContextList = (items: Array<string | undefined | null>) =>
  items
    .map((item) => (item || "").trim())
    .filter(Boolean)
    .slice(0, 8);

const getScopeSummary = (scope: HealthModelScopeDescriptor & { segment_description?: string | null }) => {
  if (scope.scope_type === "general") {
    return "General fallback health model";
  }

  if (scope.scope_type === "segment") {
    const desc = (scope as any).segment_description;
    return desc
      ? `"${scope.scope_label}" segment health model — ${desc}`
      : `"${scope.scope_label}" segment health model`;
  }

  return `${scope.scope_label} lifecycle-stage health model`;
};

export const buildHealthModelBuilderKickoffPrompt = ({
  scope,
  hasExistingDraft,
}: {
  scope: HealthModelScopeDescriptor;
  hasExistingDraft: boolean;
}) => `Start the health model builder conversation for the ${getScopeSummary(scope)}.

Introduce yourself as ORA in one short paragraph, explain how you will help shape this document, and ask one focused opening question.

${hasExistingDraft
  ? "Mention that an existing draft already exists and that you can refine, critique, or rewrite it with the user."
  : "Mention that you can help the user think through the definition even if they are unsure where to start."}

Keep the tone conversational and concise.`;

export const buildHealthModelBuilderContext = ({
  scope,
  businessContext,
  documentTitle,
  documentContent,
}: {
  scope: HealthModelScopeOption;
  businessContext?: BusinessContext | null;
  documentTitle: string;
  documentContent: string;
}) => {
  const segmentContext = scope.scope_type === "segment" && scope.segment_filter
    ? {
        segment_name: scope.scope_label,
        segment_description: scope.segment_description || null,
        segment_filter_summary: JSON.stringify(scope.segment_filter, null, 2),
      }
    : null;

  return {
    scope: {
      scope_type: scope.scope_type,
      scope_key: scope.scope_key,
      scope_label: scope.scope_label,
      scope_value: scope.scope_value,
      scope_source_label: scope.scope_source_label,
      match_field: scope.match_field || null,
    },
    ...(segmentContext ? { segment: segmentContext } : {}),
    workspace_context: {
      company_name: businessContext?.company_profile?.company_name?.trim() || null,
      company_description: businessContext?.company_profile?.description?.trim() || null,
      products: summarizeContextList((businessContext?.products || []).map((product) => product.name)),
      features: summarizeContextList((businessContext?.features || []).map((feature) => feature.name)),
      goals: summarizeContextList((businessContext?.goals || []).map((goal) => goal.name)),
      lifecycle_stages: summarizeContextList(
        (businessContext?.lifecycle_stages || []).map((stage) => stage.name),
      ),
      segments: summarizeContextList(
        (businessContext?.segments || []).map((segment) => segment.name),
      ),
    },
    current_draft: {
      title: documentTitle.trim() || null,
      content: documentContent.trim() || null,
    },
  };
};

export interface ParsedHealthDraft {
  chatContent: string;
  draftContent: string | null;
  isDraftStreaming: boolean;
}

const DRAFT_OPEN = "```health-draft\n";
const DRAFT_CLOSE = "\n```";

export function parseHealthDraftFromContent(raw: string): ParsedHealthDraft {
  const openIdx = raw.indexOf(DRAFT_OPEN);

  if (openIdx === -1) {
    return { chatContent: raw, draftContent: null, isDraftStreaming: false };
  }

  const chatContent = raw.slice(0, openIdx).trimEnd();
  const draftStart = openIdx + DRAFT_OPEN.length;
  const remaining = raw.slice(draftStart);

  // Look for the closing fence — must be ``` on its own (not part of a longer fence)
  const closeIdx = remaining.lastIndexOf(DRAFT_CLOSE);
  // Verify the closing fence is actually at the end (allow trailing whitespace)
  const isClosedProperly =
    closeIdx !== -1 && remaining.slice(closeIdx + DRAFT_CLOSE.length).trim() === "";

  if (isClosedProperly) {
    return {
      chatContent,
      draftContent: remaining.slice(0, closeIdx),
      isDraftStreaming: false,
    };
  }

  // Fence opened but not yet closed — still streaming
  return {
    chatContent,
    draftContent: remaining,
    isDraftStreaming: true,
  };
}
